/**
 * „Zarządzaj ›" — who receives this form (R22).
 *
 * A row TOGGLES: pressing a client who does not hold the form assigns them, pressing one who does
 * takes it away. One control for both directions, because „add" and „remove" as two lists would put
 * the same person in two places and make the trainer find them twice. That is also why the dialog's
 * one answer is „Gotowe" and not „Anuluj / Zapisz": every press has already been written.
 *
 * The roster comes from `entities/client`'s own `assignableClients`, which already drops the two
 * kinds of row a survey cannot reach: an offer to somebody who is not yet a client (an invited
 * roster entry IS offered — its engagement is open, R2), and a deactivated client who cannot train.
 * Re-deriving that here would be a second opinion about the same response.
 *
 * ── The row is the MOBILE app's assign row ───────────────────────────────────────────────────
 * Owner, 17 September 2026: the phone's assignment step is the drawing to follow. So the row is a
 * card carrying the person's disc, their name over one line of meta and an accent tick when they
 * hold the form, and a chosen row takes the accent on its edge — the frame the owner pointed at.
 * `AssignClientRow.tsx` in `features/intake-survey/components/builder` is the original; the
 * stylesheet records what was transcribed and the one thing that could not be.
 *
 * **The phone's „Zaznacz wszystkich" is deliberately NOT here.** On the phone the assign step is a
 * multi-select committed by a button at the end, so selecting everybody is one local change. Here a
 * press IS the write, so the same control would fire one request per client with no way back, and
 * the lede promises the opposite.
 *
 * ── The faces arrive as a PROP ───────────────────────────────────────────────────────────────
 * `assignableClients` projects a name and an address and nothing else, and the editor already holds
 * the roster's photographs for its own avatar stack. Deriving them a second time here would be a
 * second reading of one response — the count-and-faces arrangement `model/clientFaces` describes.
 *
 * ── Not optimistic, and deliberately so ──────────────────────────────────────────────────────
 * The tick follows the refetched template, so a write that fails leaves the list telling the truth
 * rather than showing an assignment the server refused. The matrix tab takes the same decision for
 * the same reason.
 */

import { useState, type RefObject } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  assignableClients,
  matchingClients,
  rosterQueryOptions,
} from '~/entities/client';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import { CheckIcon } from '~/shared/ui/AuthIcons';
import { SearchField } from '~/shared/ui/SearchField';

import type { ClientFace } from '../model/clientFace';
import type { TranslateKey } from '../model/translateKey';
import { PickerDialog } from './PickerDialog';
import styles from './PickerDialog.module.css';

export interface ClientPickerProps {
  readonly open: boolean;
  readonly assignedIds: readonly number[];
  /** The roster's photographs and initials, as the editor already read them. */
  readonly faces: ReadonlyMap<number, ClientFace>;
  readonly onAssign: (customerId: number) => void;
  readonly onUnassign: (customerId: number) => void;
  readonly onClose: () => void;
  readonly triggerRef: RefObject<HTMLElement | null>;
  readonly lang?: string;
  readonly t: TranslateKey;
}

export function ClientPicker({
  open,
  assignedIds,
  faces,
  onAssign,
  onUnassign,
  onClose,
  triggerRef,
  lang = 'pl',
  t,
}: ClientPickerProps) {
  const [term, setTerm] = useState('');
  // The same cache entry the screen's faces come from — one request, two projections.
  const roster = useQuery(rosterQueryOptions());

  const held = new Set(assignedIds);
  const rows = matchingClients(assignableClients(roster.data, lang), term);

  return (
    <PickerDialog
      open={open}
      onClose={onClose}
      title={t('web:reports.editor.clients.title')}
      lede={t('web:reports.editor.clients.lede')}
      triggerRef={triggerRef}
      t={t}
    >
      <SearchField
        value={term}
        onChange={setTerm}
        label={t('web:reports.editor.clients.searchLabel')}
        placeholder={t('web:reports.editor.clients.searchPlaceholder')}
        clearLabel={t('web:reports.editor.picker.searchClear')}
        disabled={false}
      />

      {rows.length === 0 ? (
        // „Nobody can hold this form" and „nobody matches what you typed" are two different
        // sentences, because they call for two different next moves.
        <p className={styles.empty}>
          {term.trim() === ''
            ? t('web:reports.editor.clients.empty')
            : t('web:reports.editor.clients.noResults', { term })}
        </p>
      ) : (
        <ul className={styles.list}>
          {rows.map((client) => {
            const isHeld = held.has(client.customerId);
            const face = faces.get(client.customerId);
            return (
              <li key={client.customerId}>
                <button
                  type="button"
                  className={`${String(styles.row)} ${isHeld ? String(styles.rowOn) : ''}`}
                  // A CHECKBOX, because that is what a row that can be on or off is. The state then
                  // reaches a reader through `aria-checked` rather than through the two names a
                  // toggle-as-button had to swap between.
                  role="checkbox"
                  aria-checked={isHeld}
                  onClick={() => {
                    if (isHeld) onUnassign(client.customerId);
                    else onAssign(client.customerId);
                  }}
                >
                  {/* Decorative, as everywhere: the name is immediately beside it. A client the
                    * roster could not name here has no face either, and the disc is then the two
                    * letters of nothing — which is the same empty monogram the roster draws. */}
                  <AvatarChip
                    initials={face?.initials ?? ''}
                    avatarUrl={face?.avatarUrl ?? null}
                    loading="lazy"
                  />
                  <span className={styles.rowText}>
                    <span className={styles.rowLabel}>{client.label}</span>
                    {/* Always rendered, so a client with no address keeps the row's height. */}
                    <span className={styles.rowHint}>
                      {client.email === null || client.email === ''
                        ? t('web:reports.editor.clients.noEmail')
                        : client.email}
                    </span>
                  </span>
                  {isHeld ? <CheckIcon className={styles.rowMark} /> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </PickerDialog>
  );
}
